import React, { useState, useEffect } from "react";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getExpirySettings, updateExpirySettings, DEFAULT_EXPIRY_SETTINGS } from "../../services/expiryService";

export const ExpirySettings = () => {
  const { service, addNotification, currentUser } = useApp();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsId, setSettingsId] = useState(null);
  const [newInterval, setNewInterval] = useState("");
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [service]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const loaded = await getExpirySettings(service);
      setSettings(loaded);
      setSettingsId(loaded.settingsId);
    } catch (e) {
      console.error("Error loading settings:", e);
      addNotification("Failed to load expiry settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoNotifications = async () => {
    const updated = { ...settings, enableAutoNotifications: !settings.enableAutoNotifications };
    setSettings(updated);
  };

  const handleAddInterval = () => {
    const days = parseInt(newInterval);
    if (!days || days < 1 || days > 365) {
      addNotification("Please enter a valid number between 1 and 365", "error");
      return;
    }

    if (settings.notificationIntervals.includes(days)) {
      addNotification("This interval already exists", "error");
      return;
    }

    const updated = [...settings.notificationIntervals, days].sort((a, b) => a - b);
    setSettings({ ...settings, notificationIntervals: updated });
    setNewInterval("");
    addNotification("Interval added (not saved yet)", "info");
  };

  const handleRemoveInterval = (interval) => {
    const updated = settings.notificationIntervals.filter(i => i !== interval);
    setSettings({ ...settings, notificationIntervals: updated });
  };

  const handleToggleRole = (role) => {
    const updated = settings.recipientRoles.includes(role)
      ? settings.recipientRoles.filter(r => r !== role)
      : [...settings.recipientRoles, role];
    setSettings({ ...settings, recipientRoles: updated });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const success = await updateExpirySettings(service, settingsId, {
        NotificationIntervals: settings.notificationIntervals,
        EnableAutoNotifications: settings.enableAutoNotifications,
        RecipientRoles: settings.recipientRoles,
      });

      if (success) {
        addNotification("Expiry settings saved successfully", "success");
      } else {
        addNotification("Failed to save settings", "error");
      }
    } catch (e) {
      console.error("Error saving settings:", e);
      addNotification("Error saving settings: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      setTestLoading(true);
      const html = `
        <div style="font-family: Helvetica, Arial; max-width: 600px; margin: 0 auto; padding: 20px; background: ${theme.bg.secondary};">
          <h2 style="color: ${theme.text.primary};">Test Expiry Notification</h2>
          <p style="color: ${theme.text.secondary};">This is a test notification from LexCore to verify your email settings are configured correctly.</p>
          <table style="background: ${theme.bg.primary}; padding: 16px; border-radius: 6px; width: 100%;">
            <tr><td style="padding: 8px; font-weight: 600; color: ${theme.text.primary};">Test Time:</td><td style="color: ${theme.text.primary};">${new Date().toLocaleString()}</td></tr>
            <tr><td style="padding: 8px; font-weight: 600; color: ${theme.text.primary};">Sent To:</td><td style="color: ${theme.text.primary};">${currentUser?.mail}</td></tr>
            <tr><td style="padding: 8px; font-weight: 600; color: ${theme.text.primary};">Notification Intervals:</td><td style="color: ${theme.text.primary};">${settings.notificationIntervals.join(", ")} days</td></tr>
            <tr><td style="padding: 8px; font-weight: 600; color: ${theme.text.primary};">Auto Notifications:</td><td style="color: ${theme.text.primary};">${settings.enableAutoNotifications ? "Enabled" : "Disabled"}</td></tr>
            <tr><td style="padding: 8px; font-weight: 600; color: ${theme.text.primary};">Recipient Roles:</td><td style="color: ${theme.text.primary};">${settings.recipientRoles.join(", ")}</td></tr>
          </table>
          <p style="color: ${theme.text.tertiary}; font-size: 12px; margin-top: 16px;">If you received this email, your notification settings are working correctly.</p>
        </div>
      `;

      await service.sendEmail(
        currentUser?.mail,
        "[LexCore Test] Expiry Notification Settings Test",
        html
      );

      addNotification("Test email sent to " + currentUser?.mail, "success");
    } catch (e) {
      console.error("Error sending test email:", e);
      addNotification("Failed to send test email: " + e.message, "error");
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-slate-400">Loading settings...</div>;
  }

  if (!settings) {
    return <div className="p-12 text-center text-red-600">Failed to load settings</div>;
  }

  const roles = [
    { id: "admin", label: "Admin Users" },
    { id: "requester", label: "Contract Requesters" },
    { id: "owner", label: "Contract Owners" },
    { id: "categoryManager", label: "Category Managers" },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-slate-100 mb-1">
          Expiry Notification Settings
        </h1>
        <p className="text-slate-400 m-0">Configure how the system notifies about expiring contracts</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent mt-2" />
      </div>

      <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 mb-6">
        {/* Auto Notifications Toggle */}
        <div className="mb-6 pb-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-slate-100 mb-1 text-base font-semibold">Enable Auto-Notifications</h3>
              <p className="text-slate-400 m-0 text-sm">
                System will check for expiring contracts and send notifications automatically
              </p>
            </div>
            <div
              className={`w-14 h-7 rounded-full cursor-pointer relative transition-colors duration-300 ${
                settings.enableAutoNotifications ? "bg-yellow-600" : "bg-slate-700"
              }`}
              onClick={handleToggleAutoNotifications}
            >
              <div
                className={`absolute top-0.5 w-5.5 h-5.5 rounded-full transition-all duration-300 ${
                  settings.enableAutoNotifications
                    ? "left-7 bg-slate-900"
                    : "left-0.5 bg-slate-400"
                }`}
              />
            </div>
          </div>
          {!settings.enableAutoNotifications && (
            <div className="bg-orange-900/20 border border-orange-600 rounded-md p-3 mt-3 text-orange-600 text-sm">
              ⚠️ Auto-notifications are disabled. Admins must manually check for expiring contracts.
            </div>
          )}
        </div>

        {/* Notification Intervals */}
        <div className="mb-6 pb-6 border-b border-slate-700">
          <h3 className="text-slate-100 mb-4 text-base font-semibold">Notification Intervals</h3>
          <p className="text-slate-400 mb-3 text-sm">
            Send reminders this many days before contract expiry
          </p>

          {/* Current Intervals */}
          <div className="flex flex-wrap gap-2 mb-4">
            {settings.notificationIntervals.sort((a, b) => a - b).map(interval => (
              <div key={interval} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 flex items-center gap-2 text-slate-100">
                <span className="font-mono font-semibold">{interval}d</span>
                <button
                  onClick={() => handleRemoveInterval(interval)}
                  className="bg-none border-none text-slate-400 hover:text-slate-200 cursor-pointer text-lg p-0 leading-none"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* Add New Interval */}
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              max="365"
              value={newInterval}
              onChange={(e) => setNewInterval(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAddInterval()}
              placeholder="Days before expiry"
              className="flex-1 px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600"
            />
            <button
              onClick={handleAddInterval}
              className="px-5 py-2.5 bg-yellow-600 border-none rounded text-slate-900 cursor-pointer font-semibold text-sm hover:bg-yellow-500"
            >
              Add
            </button>
          </div>
        </div>

        {/* Recipient Roles */}
        <div className="mb-6">
          <h3 className="text-slate-100 mb-4 text-base font-semibold">Who Should Be Notified?</h3>
          <p className="text-slate-400 mb-3 text-sm">
            Select which roles receive expiry notifications
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {roles.map(role => (
              <label
                key={role.id}
                className={`flex items-center gap-2.5 p-3 bg-slate-800 rounded cursor-pointer text-slate-100 border ${
                  settings.recipientRoles.includes(role.id) ? "border-yellow-600" : "border-slate-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={settings.recipientRoles.includes(role.id)}
                  onChange={() => handleToggleRole(role.id)}
                  className="accent-yellow-600 cursor-pointer"
                />
                <span className="text-sm">{role.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Test and Save Buttons */}
        <div className="flex gap-3 justify-between">
          <button
            onClick={handleTestNotification}
            disabled={testLoading}
            className={`px-5 py-3 bg-blue-600 border-none rounded text-white cursor-pointer font-semibold ${
              testLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-blue-500"
            }`}
          >
            {testLoading ? "Sending..." : "📧 Send Test Email"}
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-7 py-3 bg-green-700 border-none rounded text-white cursor-pointer font-semibold text-sm ${
              saving ? "opacity-60 cursor-not-allowed" : "hover:bg-green-600"
            }`}
          >
            {saving ? "Saving..." : "✓ Save Settings"}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-5">
        <h4 className="text-yellow-600 mb-3 text-sm font-semibold">💡 How It Works</h4>
        <ul className="text-slate-400 m-0 pl-5 leading-relaxed text-sm">
          <li>System checks for expiring contracts every hour (if enabled)</li>
          <li>Notifications are sent at the intervals you configure</li>
          <li>Only renewable contracts with an expiry date are monitored</li>
          <li>Contracts in "Executed", "Cancelled", or "Expired" status are skipped</li>
          <li>Admin users always receive escalation alerts at the 60-day mark</li>
          <li>Use test email to verify your settings are working</li>
        </ul>
      </div>
    </div>
  );
};
